import { Loading } from '@affine/component';
import { DocsService } from '@affine/core/modules/doc';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { type ReactElement, useEffect, useState } from 'react';

import {
  createLibraryItem,
  LIBRARY_KIND,
} from '../../desktop/pages/workspace/library-page/create-library-item';
import type { Citation } from '../orchestration/types';
import {
  buildCitationPdfRequest,
  buildCitationSourceProxyUrl,
  citationPdfFileName,
} from '../orchestration/utils/citation-pdf';
import { citationDisplayKey } from '../orchestration/utils/linkify-citations';
import { AttachmentPreviewPeekView } from '../peek-view/view/attachment-preview';
import * as styles from './styles.css';

interface CachedPdf {
  docId: string;
  blockId: string;
}

/** Re-opening the same citation skips the network fetch + Library write. */
const sessionCache = new Map<string, CachedPdf>();

type OpenState =
  | { kind: 'loading' }
  | { kind: 'pdf'; docId: string; blockId: string }
  | { kind: 'source'; src: string }
  | { kind: 'error' };

/**
 * Resolves and renders a citation's source inside the side panel: a loading
 * spinner while resolving, then the open-access PDF (cached to the Library,
 * rendered by the same PDFium attachment viewer used elsewhere) or the source
 * page in a sandboxed in-app web view.
 */
export const CitationContent = ({
  citation,
}: {
  citation: Citation;
}): ReactElement => {
  const docsService = useService(DocsService);
  const workspaceService = useService(WorkspaceService);
  const [state, setState] = useState<OpenState>({ kind: 'loading' });
  // Bumping this remounts the source iframe, resetting any in-page navigation
  // back to the citation's own page ("Back to citation").
  const [reloadKey, setReloadKey] = useState(0);

  const title = citation.title?.trim() || citation.identifier || 'Citation';
  const cacheKey = `${workspaceService.workspace.id}:${citationDisplayKey(
    citation.type,
    citation.identifier ?? ''
  )}`;

  useEffect(() => {
    setState({ kind: 'loading' });
    let cancelled = false;

    resolveCitationTarget(docsService, workspaceService, citation, cacheKey)
      .then(target => {
        if (!cancelled) setState(target);
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [citation, cacheKey, docsService, workspaceService]);

  return (
    <div className={styles.content}>
      {state.kind === 'loading' && (
        <div className={styles.center}>
          <Loading size={32} />
          <span className={styles.hint}>Opening {title}…</span>
        </div>
      )}
      {state.kind === 'pdf' && (
        <AttachmentPreviewPeekView
          docId={state.docId}
          blockId={state.blockId}
        />
      )}
      {state.kind === 'source' && (
        <>
          <div className={styles.sourceBar}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => setReloadKey(key => key + 1)}
            >
              ← Back to citation
            </button>
          </div>
          <iframe
            key={`${state.src}#${reloadKey}`}
            title={title}
            src={state.src}
            // `allow-same-origin` so in-page navigations send our auth cookie and
            // stay in-app; `allow-popups*` so non-embeddable links and the "Open
            // in browser" button open a real browser tab. No `allow-scripts` —
            // scripts stay blocked by `script-src 'none'` + its absence (the
            // dangerous combo is allow-same-origin *with* allow-scripts).
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            className={styles.frame}
          />
        </>
      )}
      {state.kind === 'error' && (
        <div className={styles.center}>
          <span className={styles.errorTitle}>
            Couldn&rsquo;t open this citation
          </span>
          <span className={styles.hint}>
            No source was available to open in-app.
          </span>
        </div>
      )}
    </div>
  );
};

async function resolveCitationTarget(
  docsService: DocsService,
  workspaceService: WorkspaceService,
  citation: Citation,
  cacheKey: string
): Promise<OpenState> {
  const cached = sessionCache.get(cacheKey);
  if (cached) {
    return { kind: 'pdf', docId: cached.docId, blockId: cached.blockId };
  }

  const pdfRequest = buildCitationPdfRequest(citation);
  if (pdfRequest) {
    try {
      const res = await fetch(pdfRequest, { credentials: 'include' });
      if (res.ok) {
        const bytes = await res.arrayBuffer();
        if (bytes.byteLength > 0) {
          const file = new File([bytes], citationPdfFileName(citation), {
            type: 'application/pdf',
          });
          const result = await createLibraryItem(
            { docsService, workspaceService, file },
            buildLibraryBlobIndex(docsService)
          );
          const blockId =
            result.attachmentBlockId ??
            (await resolveAttachmentBlockId(docsService, result.docId));
          if (blockId) {
            sessionCache.set(cacheKey, { docId: result.docId, blockId });
            return { kind: 'pdf', docId: result.docId, blockId };
          }
        }
      }
    } catch {
      // fall through to the source view
    }
  }

  const src = buildCitationSourceProxyUrl(citation);
  return src ? { kind: 'source', src } : { kind: 'error' };
}

async function resolveAttachmentBlockId(
  docsService: DocsService,
  docId: string
): Promise<string | undefined> {
  let handle: ReturnType<DocsService['open']>;
  try {
    handle = docsService.open(docId);
  } catch {
    return undefined;
  }
  try {
    await handle.doc.waitForSyncReady();
    const blocks =
      handle.doc.blockSuiteDoc.getBlocksByFlavour('affine:attachment');
    return blocks[0]?.id;
  } catch {
    return undefined;
  } finally {
    handle.release();
  }
}

/** blobId → docId index of existing Library items, for createLibraryItem dedup. */
function buildLibraryBlobIndex(docsService: DocsService): Map<string, string> {
  const map = new Map<string, string>();
  const docs = docsService.list.docs$.value;
  const allProps = docsService.allDocProperties$.value;
  if (!docs || !allProps) return map;
  for (const doc of docs) {
    const props = allProps[doc.id];
    if (!props || props['custom:kind'] !== LIBRARY_KIND) continue;
    const blobId = props['custom:blobId'];
    if (typeof blobId === 'string' && blobId) {
      map.set(blobId, doc.id);
    }
  }
  return map;
}
